import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Seo } from '../../lib/seo';
import { api } from '../../lib/api';
import type { Project, Service } from '../../lib/types';
import { fallbackProjects, fallbackServices } from '../../lib/static-content';
import { ServiceCard } from '../../components/ServiceCard';
import { ProjectCard } from '../../components/ProjectCard';
import { useSite } from '../../lib/site-context';

import './Module.scss';

export function HomePage() {
  const { site } = useSite();
  const [services, setServices] = useState<Service[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    api.services().then(setServices).catch(() => setServices(fallbackServices));
    api.projects().then(setProjects).catch(() => setProjects(fallbackProjects));
  }, []);

  const setting = (key: string, fallback: string) => site.settings[key] || fallback;

  const codeContent = (`const enterprise = {
      strategy,
      development,
      operations,
      security,
      reliability,
      talent
    }`);

  return (
    <>
      <Seo
        title={setting('seo.defaultTitle', 'A3GS Tec')}
        description={setting(
          'seo.defaultDescription',
          setting('site.description', 'Tecnologia, redes, servidores, DNS, segurança e desenvolvimento web.')
        )}
        path="/"
      />

      {/* HERO SECTION */}
      <section className="hero section">
        <div className="hero-grid" />
        <div className="container hero-content">
          <div>
            <span className="eyebrow">
              <span className="status-dot" />
              {setting('site.tagline', 'Tecnologia que conecta ideias a resultados')}
            </span>

            <h1>
              Criamos tecnologia para <span className="gradient-text">conectar, proteger</span> e fazer seu negócio crescer.
            </h1>

            <p>
              {setting(
                'site.description',
                'Desenvolvimento web, servidores, redes, DNS, hospedagem, segurança e soluções digitais com visão de longo prazo.'
              )}
            </p>

            <div className="hero-actions">
              <Link className="btn btn-primary btn-large" to="/contato">
                <span>Solicitar orçamento</span>
                <Icon icon="lucide:arrow-right" width="16" height="16" />
              </Link>
              <Link className="btn btn-outline btn-large" to="/portfolio">
                <span>Ver portfólio</span>
                <Icon icon="lucide:briefcase" width="16" height="16" />
              </Link>
            </div>

            <div className="hero-points">
              <span>
                <Icon icon="lucide:check-circle-2" width="14" height="14" style={{ color: 'var(--cyan)' }} />
                Soluções sob medida
              </span>
              <span>
                <Icon icon="lucide:check-circle-2" width="14" height="14" style={{ color: 'var(--cyan)' }} />
                Tecnologia moderna
              </span>
              <span>
                <Icon icon="lucide:check-circle-2" width="14" height="14" style={{ color: 'var(--cyan)' }} />
                Segurança desde o início
              </span>
            </div>
          </div>

          <div className="hero-visual" aria-hidden="true">
            <div className="orbit orbit-a" />
            <div className="orbit orbit-b" />
            <div className="code-card">
              <div className="code-top">
                <div style={{ display: 'flex', gap: '5px' }}>
                  <Icon icon="lucide:circle" width="9" height="9" style={{ fill: '#ff5f56', color: '#ff5f56' }} />
                  <Icon icon="lucide:circle" width="9" height="9" style={{ fill: '#ffbd2e', color: '#ffbd2e' }} />
                  <Icon icon="lucide:circle" width="9" height="9" style={{ fill: '#27c93f', color: '#27c93f' }} />
                </div>
                <small>a3gs.config</small>
              </div>
              <pre className="code-container">
                <code className="language-javascript">{codeContent}</code>
              </pre>
            </div>

            <span className="chip floating-chip chip-a">
              <Icon icon="lucide:globe" width="12" height="12" /> DDNS
            </span>
            <span className="chip floating-chip chip-b">
              <Icon icon="lucide:code-2" width="12" height="12" /> WEB
            </span>
            <span className="chip floating-chip chip-c">
              <Icon icon="lucide:server" width="12" height="12" /> SERVER
            </span>
            <span className="chip floating-chip chip-d">
              <Icon icon="lucide:shield-check" width="12" height="12" /> SECURITY
            </span>
          </div>
        </div>
      </section>

      {/* SEÇÃO DE SERVIÇOS / SOLUÇÕES */}
      <section className="section">
        <div className="container">
          <div className="section-heading">
            <span className="section-tag">SOLUÇÕES</span>
            <h2>Um ecossistema de tecnologia para sua empresa</h2>
            <p>Você pode expandir a lista de serviços pelo painel administrativo.</p>
          </div>

          <div className="services-grid">
            {services.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
            <Link className="service-card future-card" to="/contato">
              <div className="service-icon">
                <Icon icon="lucide:plus" width="20" height="20" />
              </div>
              <h2>Nova solução</h2>
              <p>Espaço reservado para o próximo serviço da A3GS Tec.</p>
              <span>
                Adicionar em breve <Icon icon="lucide:arrow-right" width="14" height="14" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* MÉTRICAS */}
      <section className="section metrics-section">
        <div className="container metrics-panel">
          {site.metrics.length ? (
            site.metrics.map((metric) => (
              <div key={metric.id}>
                <strong>{metric.label}</strong>
                <span>{metric.valueText}</span>
              </div>
            ))
          ) : (
            <>
              <div>
                <strong>Web</strong>
                <span>Desenvolvimento</span>
              </div>
              <div>
                <strong>DNS</strong>
                <span>Domínios e infraestrutura</span>
              </div>
              <div>
                <strong>Cloud</strong>
                <span>Servidores e serviços</span>
              </div>
              <div>
                <strong>24/7</strong>
                <span>Visão de disponibilidade</span>
              </div>
            </>
          )}
        </div>
      </section>

      {/* PORTFÓLIO */}
      <section className="section">
        <div className="container">
          <div className="section-heading">
            <span className="section-tag">PORTFÓLIO</span>
            <h2>Projetos que podem ganhar vida aqui.</h2>
          </div>

          <div className="portfolio-grid">
            {projects.slice(0, 3).map((project, i) => (
              <ProjectCard key={project.id} project={project} featured={i === 0} />
            ))}
          </div>

          <div className="center-action">
            <Link className="btn btn-outline" to="/portfolio">
              <span>Ver portfólio completo</span>
              <Icon icon="lucide:arrow-right" width="14" height="14" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA / CHAMADA PARA AÇÃO */}
      <section className="section cta-section">
        <div className="container cta-panel">
          <div>
            <span className="section-tag">VAMOS CONSTRUIR</span>
            <h2>Pronto para levar seu projeto para o próximo nível?</h2>
            <p>Conte o que você precisa e vamos desenhar o caminho mais adequado.</p>
          </div>
          <div className="cta-actions">
            <Link className="btn btn-light" to="/contato">
              <span>Falar com a A3GS</span>
              <Icon icon="lucide:message-square" width="15" height="15" />
            </Link>
            <Link className="btn btn-primary" to="/contato">
              <span>Solicitar orçamento</span>
              <Icon icon="lucide:arrow-right" width="15" height="15" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}